import { useState, type ReactElement } from "react";
import type { LinkCardParams } from "@playbookai/widget-schemas";
import { Button } from "../../atoms/Button/Button.js";
import { Link } from "../../atoms/Link/Link.js";
import { Tooltip } from "../../molecules/Tooltip/Tooltip.js";
import type { WidgetBodyProps } from "../types.js";
import { useFieldEditSession } from "../field-edit-session.js";
import { isSafeHttpUrl } from "../url-guard.js";

export interface LinkCardWidgetProps extends WidgetBodyProps<LinkCardParams> {
  /** Entity-backed Hoshin tile editor; legacy Yjs consumers stay inline. */
  readonly stagedEditing?: boolean;
}

/** Shared safe-link body. Staged editing is opt-in for Hoshin's SQL linkTile. */
export function LinkCardWidget({
  data,
  onChange,
  readOnly,
  stagedEditing = false,
}: LinkCardWidgetProps): ReactElement {
  const safe = isSafeHttpUrl(data.href);
  const [editing, setEditing] = useState(!safe && !readOnly);
  const [draft, setDraft] = useState(data);
  const [syncedData, setSyncedData] = useState(data);
  const [error, setError] = useState<string | null>(null);
  /*
   * PB-4923 AC5 — `linkTile`'s two mapped leaves, claimed only on the STAGED
   * branch. The inline branch below serves the legacy Yjs `linkCard` kind,
   * whose merges go through the collaboration document rather than the bounded
   * operation executor, so a claim there would name a target no command can
   * address. Both hooks run unconditionally; only the staged branch spreads
   * them.
   */
  const labelLease = useFieldEditSession(stagedEditing ? "label" : undefined);
  const hrefLease = useFieldEditSession(stagedEditing ? "href" : undefined);

  // Do not let an editor opened before a remote/server update later overwrite
  // that update with a stale draft. Local typing leaves `data` unchanged and is
  // therefore preserved until Save or Cancel.
  if (
    stagedEditing &&
    editing &&
    (syncedData.label !== data.label || syncedData.href !== data.href)
  ) {
    setSyncedData(data);
    setDraft(data);
  }

  // VSM and legacy Hoshin Yjs cards keep the original immediate editor. The
  // same component opts into staged editing only when rendered as `linkTile`.
  if (!stagedEditing) {
    return (
      <div className="flex h-full w-full flex-col gap-1.5 p-3">
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a bare in-card field that takes the card's own type; Input ghost adds a hover wash and a 1px edge, and no approved look row lists the change. */}
        <input
          value={data.label}
          disabled={readOnly}
          maxLength={255}
          onChange={(event) => onChange({ ...data, label: event.target.value })}
          placeholder="Link title"
          aria-label="Link title"
          className="truncate rounded-md bg-transparent text-sm font-semibold text-pb-text focus-field placeholder:text-pb-text-muted disabled:cursor-default"
        />
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a bare in-card field that takes the card's own type; Input ghost adds a hover wash and a 1px edge, and no approved look row lists the change. */}
        <input
          value={data.href}
          disabled={readOnly}
          maxLength={2048}
          onChange={(event) => onChange({ ...data, href: event.target.value })}
          placeholder="https://…"
          aria-label="Link URL"
          className="truncate rounded-md bg-transparent text-dense-1 text-pb-text-muted focus-field placeholder:text-pb-text-muted disabled:cursor-default"
        />
        <div className="mt-auto min-w-0">
          {safe ? (
            <Tooltip content={data.href} wrapperClassName="min-w-0 max-w-full">
              <Link
                href={data.href}
                external
                onPointerDown={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.stopPropagation();
                }}
                className="min-w-0 max-w-full truncate text-dense-1"
              >
                <span className="truncate">
                  {data.label.trim() || data.href}
                </span>
              </Link>
            </Tooltip>
          ) : (
            <span className="text-dense-1 text-pb-text-muted">
              Add a URL above
            </span>
          )}
        </div>
      </div>
    );
  }

  const openEditor = (): void => {
    setDraft(data);
    setSyncedData(data);
    setError(null);
    setEditing(true);
  };
  const cancel = (): void => {
    setDraft(data);
    setError(null);
    setEditing(false);
  };
  const save = (): void => {
    const next = { label: draft.label.trim(), href: draft.href.trim() };
    if (next.href && !isSafeHttpUrl(next.href)) {
      setError("Enter a valid http or https URL.");
      return;
    }
    setError(null);
    onChange(next);
    setEditing(false);
  };

  if (editing && !readOnly) {
    return (
      <div className="flex h-full w-full flex-col gap-2 p-3">
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a 30px field with a 4px corner; Input size="sm" is a 28px field with the 8px corner, and no approved look row lists the change. */}
        <input
          value={draft.label}
          maxLength={255}
          {...labelLease}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              label: event.target.value,
            }))
          }
          placeholder="Link title"
          aria-label="Link title"
          className="rounded border border-pb-control-border bg-pb-card px-2 py-1 text-sm font-semibold text-pb-text focus-frame placeholder:text-pb-text-muted"
        />
        {/* eslint-disable-next-line ds/no-hand-built -- kept: a 30px field with a 4px corner; Input size="sm" is a 28px field with the 8px corner, and no approved look row lists the change. */}
        <input
          value={draft.href}
          maxLength={2048}
          {...hrefLease}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              href: event.target.value,
            }))
          }
          placeholder="https://…"
          aria-label="Link URL"
          className="rounded border border-pb-control-border bg-pb-card px-2 py-1 text-dense-1 text-pb-text focus-frame placeholder:text-pb-text-muted"
        />
        {error ? (
          <span role="alert" className="text-dense-1 text-pb-red-text">
            {error}
          </span>
        ) : null}
        <div className="mt-auto flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={cancel}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" onClick={save}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group/link relative flex h-full w-full flex-col gap-1.5 p-3">
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-pb-text">
          {data.label.trim() || "Untitled link"}
        </div>
        <div className="truncate text-dense-1 text-pb-text-muted">
          {data.href || "No URL added"}
        </div>
      </div>
      <div className="mt-auto flex min-w-0 items-end justify-between gap-2">
        {safe ? (
          <Tooltip content={data.href} wrapperClassName="min-w-0 max-w-full">
            <Link
              href={data.href}
              external
              className="min-w-0 max-w-full truncate text-dense-1"
            >
              <span className="truncate">{data.label.trim() || data.href}</span>
            </Link>
          </Tooltip>
        ) : (
          <span className="text-dense-1 text-pb-text-muted">
            Add a URL above
          </span>
        )}
        {readOnly ? null : (
          // eslint-disable-next-line ds/no-hand-built -- kept: a 10px-text overlay chip on the raised fill that appears on hover; no Button size or variant paints it, and no approved look row lists the change.
          <button
            type="button"
            onClick={openEditor}
            className="shrink-0 rounded bg-pb-card-elevated px-2 py-1 text-dense-2 font-medium text-pb-text-muted opacity-0 transition-opacity hover:text-pb-text group-hover/link:opacity-100 focus-visible:opacity-100 focus-ring"
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
}
